import { ContourTexture } from '../../../data/structure/ContourTexture';

/**
 * Compute a metric signed distance field (in mm) from a Uint8 scalar field using Fast Marching Method (FMM).
 *
 * The sign is derived from the input relative to `threshold`:
 * - Values >= threshold are treated as inside (positive distances).
 * - Values < threshold are treated as outside (negative distances).
 *
 * Distances are computed in physical units using anisotropic spacing `hx,hy,hz`. The algorithm uses sub-voxel
 * edge seeding by detecting threshold crossings between adjacent voxels and initializing the front with an
 * interpolated crossing position. Propagation solves the anisotropic Eikonal equation (upwind) via
 * `solveEikonal3D`.
 *
 * @param data          Input volume (flat Uint8Array or array of z-slices).
 * @param nx,ny,nz      Volume dimensions.
 * @param threshold     Zero-crossing intensity (surface at value == threshold), e.g. 127.5.
 * @param hx,hy,hz      Physical voxel spacing in mm along x,y,z.
 * @param maxDistanceMm Maximum distance (mm) to compute; values beyond are left as `maxDistanceMm`.
 * @returns             Flat Float32Array of length nx*ny*nz containing signed distances in mm.
 */
export function fmmSignedDistanceMm(
    data: Uint8Array | Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    threshold: number,
    hx: number,
    hy: number,
    hz: number,
    maxDistanceMm: number
): Float32Array {
    const accessor = makeVolumeAccessor(data, nx, ny);
    const n = nx * ny * nz;

    const origSign = new Int8Array(n);
    for (let z = 0; z < nz; z++) {
        for (let y = 0; y < ny; y++) {
            for (let x = 0; x < nx; x++) {
                const v = accessor(x, y, z);
                origSign[idx3(nx, ny, x, y, z)] = v - threshold >= 0 ? 1 : -1;
            }
        }
    }

    const phi = new Float32Array(n);
    phi.fill(Infinity);
    const state = new Uint8Array(n);
    state.fill(FAR);
    const heap = new MinHeap(n);

    function seedEdge(
        x1: number,
        y1: number,
        z1: number,
        v1: number,
        x2: number,
        y2: number,
        z2: number,
        v2: number,
        stepMm: number
    ) {
        const s1 = v1 - threshold,
            s2 = v2 - threshold;
        if (s1 === 0 || s2 === 0 || s1 * s2 < 0) {
            const denom = v2 - v1;
            const t = denom === 0 ? 0.5 : (threshold - v1) / denom;
            const d1 = Math.abs(t) * stepMm;
            const d2 = Math.abs(1 - t) * stepMm;

            const id1 = idx3(nx, ny, x1, y1, z1);
            const id2 = idx3(nx, ny, x2, y2, z2);

            if (d1 < phi[id1]) {
                phi[id1] = d1;
                state[id1] = KNOWN;
            }
            if (d2 < phi[id2]) {
                phi[id2] = d2;
                state[id2] = KNOWN;
            }
        }
    }

    for (let z = 0; z < nz; z++) {
        for (let y = 0; y < ny; y++) {
            for (let x = 0; x < nx; x++) {
                const v = accessor(x, y, z);
                if (x < nx - 1) {
                    seedEdge(
                        x,
                        y,
                        z,
                        v,
                        x + 1,
                        y,
                        z,
                        accessor(x + 1, y, z),
                        hx
                    );
                }
                if (y < ny - 1) {
                    seedEdge(
                        x,
                        y,
                        z,
                        v,
                        x,
                        y + 1,
                        z,
                        accessor(x, y + 1, z),
                        hy
                    );
                }
                if (z < nz - 1) {
                    seedEdge(
                        x,
                        y,
                        z,
                        v,
                        x,
                        y,
                        z + 1,
                        accessor(x, y, z + 1),
                        hz
                    );
                }
            }
        }
    }

    function updateNeighbor(x: number, y: number, z: number) {
        if (x < 0 || x >= nx || y < 0 || y >= ny || z < 0 || z >= nz) {
            return;
        }
        const id = idx3(nx, ny, x, y, z);
        if (state[id] === KNOWN) {
            return;
        }

        // For each axis, take the minimum of the two directional neighbors.
        // The Eikonal solver expects at most one value per axis (upwind direction).
        const vals: { T: number; h: number }[] = [];

        let bestX = Infinity;
        if (x > 0) {
            const idm = idx3(nx, ny, x - 1, y, z);
            if (state[idm] === KNOWN) {
                bestX = phi[idm];
            }
        }
        if (x < nx - 1) {
            const idp = idx3(nx, ny, x + 1, y, z);
            if (state[idp] === KNOWN && phi[idp] < bestX) {
                bestX = phi[idp];
            }
        }
        if (bestX < Infinity) {
            vals.push({ T: bestX, h: hx });
        }

        let bestY = Infinity;
        if (y > 0) {
            const idm = idx3(nx, ny, x, y - 1, z);
            if (state[idm] === KNOWN) {
                bestY = phi[idm];
            }
        }
        if (y < ny - 1) {
            const idp = idx3(nx, ny, x, y + 1, z);
            if (state[idp] === KNOWN && phi[idp] < bestY) {
                bestY = phi[idp];
            }
        }
        if (bestY < Infinity) {
            vals.push({ T: bestY, h: hy });
        }

        let bestZ = Infinity;
        if (z > 0) {
            const idm = idx3(nx, ny, x, y, z - 1);
            if (state[idm] === KNOWN) {
                bestZ = phi[idm];
            }
        }
        if (z < nz - 1) {
            const idp = idx3(nx, ny, x, y, z + 1);
            if (state[idp] === KNOWN && phi[idp] < bestZ) {
                bestZ = phi[idp];
            }
        }
        if (bestZ < Infinity) {
            vals.push({ T: bestZ, h: hz });
        }

        if (!vals.length) {
            return;
        }

        const T = solveEikonal3D(vals);
        if (T <= maxDistanceMm) {
            const newT = Math.min(phi[id], T);
            if (newT < phi[id]) {
                phi[id] = newT;
                heap.pushOrUpdate(id, newT);
                state[id] = TRIAL;
            }
        }
    }

    for (let i = 0; i < n; i++) {
        if (state[i] !== KNOWN) {
            continue;
        }
        const z = Math.floor(i / (nx * ny));
        const rem = i - z * nx * ny;
        const y = Math.floor(rem / nx);
        const x = rem - y * nx;
        updateNeighbor(x - 1, y, z);
        updateNeighbor(x + 1, y, z);
        updateNeighbor(x, y - 1, z);
        updateNeighbor(x, y + 1, z);
        updateNeighbor(x, y, z - 1);
        updateNeighbor(x, y, z + 1);
    }

    while (!heap.empty()) {
        const node = heap.popMin()!;
        const id = node.idx;
        if (state[id] === KNOWN) {
            continue;
        }
        state[id] = KNOWN;
        if (phi[id] > maxDistanceMm) {
            break;
        }

        const z = Math.floor(id / (nx * ny));
        const rem = id - z * nx * ny;
        const y = Math.floor(rem / nx);
        const x = rem - y * nx;

        updateNeighbor(x - 1, y, z);
        updateNeighbor(x + 1, y, z);
        updateNeighbor(x, y - 1, z);
        updateNeighbor(x, y + 1, z);
        updateNeighbor(x, y, z - 1);
        updateNeighbor(x, y, z + 1);
    }

    // apply sign
    for (let i = 0; i < n; i++) {
        let d = phi[i];
        if (d === Infinity) {
            d = maxDistanceMm;
        }
        phi[i] = d * origSign[i];
    }
    return phi;
}

export function quantizeSdfMmToUint8(
    sdfMm: Float32Array,
    nx: number,
    ny: number,
    nz: number,
    threshold: number = ContourTexture.SDF_THRESHOLD,
    falloffMm: number = ContourTexture.DEFAULT_MARGIN
): Uint8Array[] {
    const out: Uint8Array[] = new Array(nz);
    for (let z = 0; z < nz; z++) {
        out[z] = new Uint8Array(nx * ny);
    }

    const scale = threshold / falloffMm;
    const plane = nx * ny;

    for (let i = 0; i < sdfMm.length; i++) {
        let d = sdfMm[i];
        if (d > falloffMm) {
            d = falloffMm;
        } else if (d < -falloffMm) {
            d = -falloffMm;
        }

        let q = threshold + Math.round(d * scale);
        if (q < 0) {
            q = 0;
        } else if (q > 255) {
            q = 255;
        }
        out[Math.floor(i / plane)][i % plane] = q;
    }
    return out;
}

const FAR = 0,
    TRIAL = 1,
    KNOWN = 2;

type HeapNode = { idx: number; key: number };

class MinHeap {
    heap: HeapNode[] = [];
    pos: Int32Array;
    constructor(sizeEstimate: number) {
        this.pos = new Int32Array(sizeEstimate);
        this.pos.fill(-1);
    }
    private swap(i: number, j: number) {
        const hi = this.heap[i],
            hj = this.heap[j];
        this.pos[hi.idx] = j;
        this.pos[hj.idx] = i;
        this.heap[i] = hj;
        this.heap[j] = hi;
    }
    private up(i: number) {
        while (i > 0) {
            const p = (i - 1) >> 1;
            if (this.heap[i].key < this.heap[p].key) {
                this.swap(i, p);
                i = p;
            } else {
                break;
            }
        }
    }
    private down(i: number) {
        const n = this.heap.length;
        const running = true;
        while (running) {
            const l = i * 2 + 1,
                r = l + 1;
            let s = i;
            if (l < n && this.heap[l].key < this.heap[s].key) {
                s = l;
            }
            if (r < n && this.heap[r].key < this.heap[s].key) {
                s = r;
            }
            if (s === i) {
                break;
            }
            this.swap(i, s);
            i = s;
        }
    }
    pushOrUpdate(idx: number, key: number) {
        const p = this.pos[idx];
        if (p === -1) {
            this.heap.push({ idx, key });
            const i = this.heap.length - 1;
            this.pos[idx] = i;
            this.up(i);
        } else if (key < this.heap[p].key) {
            this.heap[p].key = key;
            this.up(p);
        }
    }
    popMin(): HeapNode | undefined {
        if (!this.heap.length) {
            return;
        }
        const min = this.heap[0];
        const last = this.heap.pop()!;
        this.pos[min.idx] = -1;
        if (this.heap.length > 0) {
            this.heap[0] = last;
            this.pos[last.idx] = 0;
            this.down(0);
        }
        return min;
    }
    empty() {
        return this.heap.length === 0;
    }
}

function idx3(nx: number, ny: number, x: number, y: number, z: number) {
    return (z * ny + y) * nx + x;
}

function makeVolumeAccessor(
    data: Uint8Array | Uint8Array[],
    nx: number,
    ny: number
) {
    if (Array.isArray(data)) {
        return (x: number, y: number, z: number) => data[z][y * nx + x];
    } else {
        return (x: number, y: number, z: number) => data[idx3(nx, ny, x, y, z)];
    }
}

function solveEikonal3D(vals: { T: number; h: number }[]): number {
    vals.sort((a, b) => a.T - b.T);
    for (let k = 1; k <= vals.length; k++) {
        const sub = vals.slice(0, k);
        let A = 0,
            B = 0,
            C = -1;
        for (const { T, h } of sub) {
            const hh = h * h;
            A += 1 / hh;
            B += T / hh;
            C += (T * T) / hh;
        }
        const disc = B * B - A * C;
        if (disc < 0) {
            continue;
        }
        const T = (B + Math.sqrt(disc)) / A;
        const Tmax = Math.max(...sub.map((v) => v.T));
        if (T >= Tmax - 1e-9) {
            return T;
        }
    }
    const best = vals.reduce((a, b) => (a.T < b.T ? a : b));
    return best.T + best.h;
}
